/** ACT-040: Collaboration cohorts — ggj_b2b_collaboration_cohorts + cohort_members */
import { supabase } from '../client';

export interface CohortMember {
  id: string;
  cohort_id: string;
  candidate_id: string;
  fitment_score: number | null;
  added_at: string;
  candidate?: { id: string; full_name: string; email: string | null; current_title: string | null };
}

export interface Cohort {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  owner_id: string | null;
  is_active: boolean;
  board_url: string | null;
  created_at: string;
  updated_at: string;
  members?: CohortMember[];
}

export const cohortService = {
  async list(orgId: string): Promise<Cohort[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_collaboration_cohorts')
      .select('*')
      .eq('org_id', orgId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as Cohort[];
  },

  async create(orgId: string, ownerId: string | null, name: string, description?: string): Promise<Cohort> {
    const { data, error } = await supabase
      .from('ggj_b2b_collaboration_cohorts')
      .insert({ org_id: orgId, owner_id: ownerId, name, description: description ?? null, is_active: true })
      .select()
      .single();
    if (error) throw error;
    return data as Cohort;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_collaboration_cohorts')
      .update({ is_active: false })
      .eq('id', id);
    if (error) throw error;
  },

  async update(
    id: string,
    patch: { name?: string; description?: string | null; board_url?: string | null }
  ): Promise<void> {
    const { error } = await supabase.from('ggj_b2b_collaboration_cohorts').update(patch).eq('id', id);
    if (error) throw error;
  },

  async listMembers(cohortId: string): Promise<CohortMember[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_cohort_members')
      .select(`
        id, cohort_id, candidate_id, fitment_score, added_at,
        candidate:ggj_b2b_candidates(id, full_name, email, current_title)
      `)
      .eq('cohort_id', cohortId)
      .order('added_at');
    if (error) throw error;
    return (data ?? []) as CohortMember[];
  },

  async addMember(cohortId: string, candidateId: string, fitmentScore?: number): Promise<CohortMember> {
    const { data, error } = await supabase
      .from('ggj_b2b_cohort_members')
      .upsert(
        { cohort_id: cohortId, candidate_id: candidateId, fitment_score: fitmentScore ?? null },
        { onConflict: 'cohort_id,candidate_id' }
      )
      .select(`id, cohort_id, candidate_id, fitment_score, added_at,
        candidate:ggj_b2b_candidates(id, full_name, email, current_title)`)
      .single();
    if (error) throw error;
    return data as CohortMember;
  },

  async removeMember(memberId: string): Promise<void> {
    const { error } = await supabase.from('ggj_b2b_cohort_members').delete().eq('id', memberId);
    if (error) throw error;
  },
};
